import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { updateSearchParams, useSearchParams } from '@ctw/shared/utils/search-params';
import { type Dispatch, type SetStateAction, useCallback } from 'react';

const RISK_GAP_RESOURCE_TYPE_KEY = 'detailResourceType';
const RISK_GAP_RESOURCE_ID_KEY = 'detailResourceId';

/** State that we want to initialize and track inside of our search params for risk-gaps v2. */
export function useSearchParamsStateForRiskGaps(): [
  string | null,
  Dispatch<SetStateAction<string | null>>,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  // set common risk gap values in the window.location.search
  const updateResourceIdSearchParam = useCallback(
    (newResourceId: string | null) => {
      setSearchParams(
        (currentParams) => {
          if (newResourceId) {
            currentParams.set(RISK_GAP_RESOURCE_TYPE_KEY, 'MeasureReport');
            currentParams.set(RISK_GAP_RESOURCE_ID_KEY, newResourceId);
          } else {
            currentParams.delete(RISK_GAP_RESOURCE_TYPE_KEY);
            currentParams.delete(RISK_GAP_RESOURCE_ID_KEY);
          }
          return currentParams;
        },
        {
          // don't add more state to the navigation history.
          replace: true,
          // If we've scrolled to find the resource, attempt to keep it on screen.
          preventScrollReset: true,
        },
      );
    },
    [setSearchParams],
  );

  const selectedRiskGapId = searchParams.get(RISK_GAP_RESOURCE_ID_KEY);
  const setSelectedRiskGapId: Dispatch<SetStateAction<string | null>> = useCallback(
    (newValueOrFn: SetStateAction<string | null>) => {
      updateResourceIdSearchParam(
        typeof newValueOrFn === 'function' ? newValueOrFn(selectedRiskGapId) : newValueOrFn,
      );
    },
    [updateResourceIdSearchParam, selectedRiskGapId],
  );
  return [selectedRiskGapId, setSelectedRiskGapId];
}

/** Use this hook for navigating to the new risk gaps v2 experimental page */
export function useNavigateToRiskGapsV2(): (record: RiskGapModel) => void {
  const { navigate } = useZuiContext();
  const navigateToRiskGapV2 = useCallback(
    (record: RiskGapModel) => {
      navigate(
        `risk-gaps?${updateSearchParams((currentParams) => {
          currentParams.set(RISK_GAP_RESOURCE_TYPE_KEY, record.type);
          currentParams.set(RISK_GAP_RESOURCE_ID_KEY, record.id);
          return currentParams;
        })}`,
      );
    },
    [navigate],
  );
  return navigateToRiskGapV2;
}
